import { useState, useCallback, useLayoutEffect } from 'react';
import { Avatar, CloseIcon } from '@halo/ui-chat-react';
import { Box, Image, Text, VStack } from '@chakra-ui/react';
import { Dialog } from '@halo/ui-react';
import type { User } from '@halo/chat-sdk-react';
import { type SessionStore } from '@halo/chat-sdk-react';
import transferHeaderImg from '@/assets/images/transfer/transferHeader.png';
import { formatAddress } from '../helpers.ts';
import { TransferForm } from './TransferForm.tsx';
import type { TransferFormValues } from './TransferForm.tsx';

interface LuckyDialogProps {
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  sessionStore?: SessionStore;
  sendTo?: User;
  onConfirm: (data: any) => void;
}
export function TransferDialog({
  isOpen,
  setIsOpen,
  sendTo,
  sessionStore,
  onConfirm,
}: LuckyDialogProps) {
  const [open, setOpen] = useState(isOpen);
  const { client, user, session } = sessionStore || {};
  const [toAddresses, setToAddresses] = useState<Record<string, string>>();
  const [toAddress, setToAddress] = useState('');

  const handleConfirm = useCallback(
    async (transferInfo: TransferFormValues & Record<string, any>) => {
      const info = {
        ...transferInfo,
        chat_id: session?.chat_id,
        user,
        sendTo,
      };
      onConfirm(info);
    },
    [session?.chat_id, user, sendTo, onConfirm]
  );

  const handleCancel = useCallback(() => {
    setOpen(false);
    setIsOpen?.(false);
  }, [setIsOpen]);

  useLayoutEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (sendTo && client?.Api && open) {
      client?.Api.common
        .getUserAddress({ user_id: sendTo.user_id })
        .then((res) => {
          setToAddresses(res.data.wallet_address);
        })
        .catch(() => {
          setToAddresses({});
        });
    }
  }, [client?.Api, sendTo, open]);

  return (
    <Dialog.Root
      placement="center"
      open={open}
      onOpenChange={(details) => {
        setOpen(details.open);
        setIsOpen?.(details.open);
      }}
      lazyMount
      unmountOnExit
      restoreFocus={false}
      trapFocus={false}
      closeOnInteractOutside={false}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner closeOnInteractOutside={false}>
        <Dialog.Content
          borderRadius="2xl"
          w="440px"
          maxW="100%"
          p="0"
          overflow="hidden"
          bg="grey.10"
        >
          <Dialog.Header
            fontSize="md"
            fontWeight="semibold"
            justifyContent="space-between"
            py="2.5"
            px="0"
            minH="154px"
            pos="relative"
            h="352px"
          >
            <Image src={transferHeaderImg} alt="img" w="full" />
            <Box
              pos="absolute"
              top="4"
              right="4"
              zIndex="1"
              justifyContent="flex-end"
              color="fg.muted"
              cursor="pointer"
              onClick={handleCancel}
            >
              <CloseIcon />
            </Box>
            <VStack pos="absolute" top="54px" left="0" w="full" gap="0">
              <Text fontSize="lg" lineHeight="normal" fontWeight="semibold">
                Transfer
              </Text>
              <Avatar
                mt="6"
                mb="3"
                src={sendTo?.avatar}
                name={sendTo?.name}
                w="20"
                height="20"
                activeShake={false}
              />
              <Text fontSize="lg" lineHeight="normal" fontWeight="semibold" maxW="200px" truncate>
                {sendTo?.name}
              </Text>
              <Text color="grey.95" fontSize="xs">
                {formatAddress(toAddress)}
              </Text>
            </VStack>
          </Dialog.Header>
          <Dialog.Body p="0">
            <TransferForm
              toAddresses={toAddresses}
              onConfirm={handleConfirm}
              setToAddress={setToAddress}
            />
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
